import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface AdminUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  is_admin: boolean;
}

/**
 * Hook to check if the current user is an admin
 * Returns admin status and user info if admin
 */
export function useAdminAuth() {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user) {
          setAdminUser(null);
          setIsLoading(false);
          return;
        }

        // Check if user is in the admins table
        const { data: adminCheck, error: adminError } = await supabase
          .from('admins')
          .select(`
            user_id,
            user_profiles!inner(
              id,
              email,
              first_name,
              last_name,
              avatar_url
            )
          `)
          .eq('user_id', session.user.id)
          .limit(1);

        if (adminError) {
          console.error('Admin auth check error:', adminError);
          setError(adminError);
          setAdminUser(null);
          return;
        }

        if (!adminCheck || adminCheck.length === 0) {
          setAdminUser(null);
          return;
        }

        const adminData = adminCheck[0];
        const userProfile = adminData.user_profiles as {
          id: string;
          email: string;
          first_name: string;
          last_name: string;
          avatar_url?: string;
        };

        setAdminUser({
          id: userProfile.id,
          email: userProfile.email || '',
          first_name: userProfile.first_name || '',
          last_name: userProfile.last_name || '',
          avatar_url: userProfile.avatar_url,
          is_admin: true,
        });
      } catch (err) {
        console.error('Admin auth check failed:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setAdminUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAdmin();
  }, []);

  return { adminUser, isLoading, error };
}

/**
 * Hook to check if current user can access admin features
 */
export function useIsAdmin() {
  const { adminUser, isLoading } = useAdminAuth();
  return { isAdmin: !!adminUser, isLoading };
}

